import Vue from 'vue'
import VueI18n from 'vue-i18n'
import cookie from '@/packages/vue-cookie'
import { DateTime, Settings } from 'luxon'
import moment from '@/packages/moment'
import { changeValidatorLocale } from '@/packages/vee-validate'
import { locales } from '@/locales'

Vue.use(VueI18n)

export const availableLocales = []
export let babel

export async function createI18n () {
	try {
		// ✅ Get all available locales from context
		const localesContext = require.context('@/locales', true, /[A-Za-z0-9-_,\s]+\.json$/i, 'weak')

		localesContext.keys().forEach(key => {
			const matched = key.match(/([A-Za-z0-9-_]+)\./i)
			if (matched && matched.length > 1) {
				availableLocales.push(matched[1])
			}
		})

		// ✅ Initialize VueI18n with proper config
		babel = new VueI18n({
			locale: 'en', // Set default locale
			fallbackLocale: process.env.VUE_APP_I18N_FALLBACK_LOCALE || 'en',
			messages: {}, // Initialize empty messages object
			silentTranslationWarn: process.env.NODE_ENV === 'production',
			silentFallbackWarn: process.env.NODE_ENV === 'production'
		})

		// ✅ Determine which locale to use
		const cookieLocale = cookie.get('app-locale')
		const envLocale = process.env.VUE_APP_I18N_LOCALE || 'en'
		const targetLocale = cookieLocale || envLocale

		// ✅ Load the target locale
		await changeI18nLocale(targetLocale, babel)

		return babel
	} catch (error) {
		console.error('Error creating i18n:', error)

		// ✅ Fallback i18n instance
		babel = new VueI18n({
			locale: 'en',
			fallbackLocale: 'en',
			messages: {
				en: {
					common: {
						fields: {}
					}
				}
			}
		})

		return babel
	}
}

function setI18nLanguage (lang, i18n) {
	try {
		Settings.defaultLocale = lang
		i18n.locale = lang
		moment.locale(lang)

		const cookieOpts = {
			expires: '1Y'
		}
		if (process.env.NODE_ENV === 'production') {
			cookieOpts.domain = '.recar.lt'
		}

		cookie.set('app-locale', lang, cookieOpts)
		document.querySelector('html').setAttribute('lang', lang)
		DateTime.local().setLocale(lang)

		return lang
	} catch (error) {
		console.error('Error setting i18n language:', error)
		return lang
	}
}

export async function changeI18nLocale (locale, i18n) {
	try {
		let i18nLocale
		const fallbackLocale = process.env.VUE_APP_I18N_FALLBACK_LOCALE || 'en'

		if (!locales[locale]) {
			console.warn(`Locale '${locale}' not found, falling back to 'en'`)
			locale = 'en'
		}

		// Always load from source locales to ensure fresh messages
		const existingMessages = i18n.getLocaleMessage(locale)
		if (!existingMessages || Object.keys(existingMessages).length === 0) {
			i18nLocale = locales[locale] || {}
		} else {
			i18nLocale = existingMessages
		}

		if (i18nLocale) {
			const fallbackMessages = locale === fallbackLocale
				? {}
				: (i18n.getLocaleMessage(fallbackLocale).common ? i18n.getLocaleMessage(fallbackLocale) : locales[fallbackLocale])
			const mergedLocale = mergeLocaleMessages(fallbackMessages || {}, i18nLocale)
			const validatorAttributes = mergeLocaleMessages({}, mergedLocale.common?.fields || {})
			changeValidatorLocale(locale, validatorAttributes)
			i18n.setLocaleMessage(locale, mergedLocale)
		}

		return Promise.resolve(setI18nLanguage(locale, i18n))
	} catch (error) {
		console.error('Error changing i18n locale:', error)
		return Promise.resolve('en')
	}
}

export function isLocaleAvailable (locale) {
	return availableLocales.includes(locale) || locales.hasOwnProperty(locale)
}

export function getCurrentLocale () {
	return babel ? babel.locale : 'en'
}

function mergeLocaleMessages (base, override) {
	if (Array.isArray(base) || Array.isArray(override)) {
		return override
	}

	if (typeof override === 'string' && override.trim() === '') {
		return base
	}

	if (!isPlainObject(base) || !isPlainObject(override)) {
		return override === undefined ? base : override
	}

	return Object.keys({
		...base,
		...override
	}).reduce((result, key) => {
		result[key] = mergeLocaleMessages(base[key], override[key])
		return result
	}, {})
}

function isPlainObject (value) {
	return value !== null && typeof value === 'object' && !Array.isArray(value)
}
